import { showAppAlert } from "./alert-context";

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

interface TelegramWebApp {
  ready(): void;
  expand(): void;
  close(): void;
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      language_code?: string;
    };
    start_param?: string;
  };
  platform?: string;
  colorScheme: "light" | "dark";
  themeParams: {
    bg_color?: string;
    text_color?: string;
    hint_color?: string;
    link_color?: string;
    button_color?: string;
    button_text_color?: string;
    secondary_bg_color?: string;
  };
  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    isProgressVisible: boolean;
    show(): void;
    hide(): void;
    enable(): void;
    disable(): void;
    showProgress(leaveActive?: boolean): void;
    hideProgress(): void;
    setText(text: string): void;
    onClick(callback: () => void): void;
    offClick(callback: () => void): void;
  };
  BackButton: {
    isVisible: boolean;
    show(): void;
    hide(): void;
    onClick(callback: () => void): void;
    offClick(callback: () => void): void;
  };
  showAlert(message: string, callback?: () => void): void;
  showConfirm(message: string, callback?: (confirmed: boolean) => void): void;
  showPopup(
    params: {
      title?: string;
      message: string;
      buttons?: Array<{
        id?: string;
        type?: "default" | "ok" | "close" | "cancel" | "destructive";
        text?: string;
      }>;
    },
    callback?: (buttonId: string) => void,
  ): void;
  HapticFeedback: {
    impactOccurred(
      style: "light" | "medium" | "heavy" | "rigid" | "soft",
    ): void;
    notificationOccurred(type: "error" | "success" | "warning"): void;
    selectionChanged(): void;
  };
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  // Fullscreen API (Bot API 8.0+)
  requestFullscreen?(): void;
  exitFullscreen?(): void;
  isFullscreen?: boolean;
  // Home Screen API (Bot API 8.0+)
  addToHomeScreen?(): void;
  checkHomeScreenStatus?(callback?: (status: HomeScreenStatus) => void): void;
  // Version check
  version?: string;
  isVersionAtLeast?(version: string): boolean;
  // Events
  onEvent?(eventType: string, eventHandler: () => void): void;
  offEvent?(eventType: string, eventHandler: () => void): void;
}

/**
 * Home screen status for Mini App
 * @see https://core.telegram.org/bots/webapps#checkHomeScreenStatus
 */
export type HomeScreenStatus = 
  | "unsupported"  // Feature not supported on this device
  | "unknown"      // Supported, can add, but can't determine if already added
  | "added"        // Already added to home screen
  | "missed";      // Not added to home screen

// Initialize Telegram WebApp on load
let _initialized = false;

function initTelegram(): TelegramWebApp | null {
  const tg = window.Telegram?.WebApp;
  if (tg && !_initialized) {
    _initialized = true;
    tg.ready();
    tg.expand();
    // Fullscreen только в нативном Mini App (iOS/Android), не в веб-клиенте (6.0 и др.)
    if (
      !isTelegramWebClient(tg) &&
      typeof tg.requestFullscreen === "function"
    ) {
      try {
        tg.requestFullscreen();
      } catch {
        // Игнорируем, если клиент не поддерживает (напр. старая версия)
      }
    }
  }
  return tg ?? null;
}

// Initialize immediately if available
if (typeof window !== "undefined") {
  initTelegram();
}

/** Веб-клиент Telegram (6.0 и др.): platform === "web" | "weba". Fullscreen не поддерживается. */
export function isTelegramWebClient(tg: TelegramWebApp): boolean {
  const p = (tg.platform ?? "").toLowerCase();
  return p === "web" || p === "weba";
}

export function getTelegram(): TelegramWebApp | null {
  return initTelegram();
}

export function getInitData(): string {
  return getTelegram()?.initData ?? "";
}

export function getUser() {
  return getTelegram()?.initDataUnsafe.user ?? null;
}

/**
 * Check if running inside Telegram Mini App
 */
export function isTelegramMiniApp(): boolean {
  const tg = getTelegram();
  return !!(tg && tg.initData && tg.initData.length > 0);
}

export function haptic(
  type:
    | "success"
    | "error"
    | "warning"
    | "light"
    | "medium"
    | "heavy"
    | "selection",
) {
  const tg = getTelegram();
  if (tg) {
    switch (type) {
      case "success":
      case "error":
      case "warning":
        tg.HapticFeedback.notificationOccurred(type);
        break;
      case "light":
      case "medium":
      case "heavy":
        tg.HapticFeedback.impactOccurred(type);
        break;
      case "selection":
        tg.HapticFeedback.selectionChanged();
        break;
    }
    return;
  }

  if (typeof navigator.vibrate !== "function") return;
  try {
    if (type === "success" || type === "error" || type === "warning") {
      navigator.vibrate([10, 50, 10]);
    } else {
      navigator.vibrate(10);
    }
  } catch {
    // Vibration API may be restricted (e.g. iOS < 18)
  }
}

/**
 * Show alert using custom AlertModal.
 * Falls back to native alert() only if AlertProvider is not mounted.
 */
export function safeShowAlert(message: string, callback?: () => void): void {
  showAppAlert(message, "info", callback);
}

/**
 * Show confirm; falls back to window.confirm() if showPopup is not supported (e.g. Web App 6.0).
 */
export function safeShowConfirm(
  message: string,
  callback: (confirmed: boolean) => void,
): void {
  const tg = getTelegram();
  try {
    if (tg?.showConfirm) {
      tg.showConfirm(message, callback);
      return;
    }
  } catch {
    // showPopup not supported in some Web App versions (e.g. 6.0)
  }
  const ok = window.confirm(message);
  callback(ok);
}

/**
 * Check if Telegram Mini App supports addToHomeScreen (Bot API 8.0+)
 * NOTE: This adds a shortcut that opens Mini App INSIDE Telegram, not a standalone PWA!
 */
export function supportsAddToHomeScreen(): boolean {
  const tg = getTelegram();
  if (!tg) return false;
  // Check if method exists and version is at least 8.0
  return typeof tg.addToHomeScreen === "function";
}

/**
 * Add Mini App shortcut to home screen using Telegram's native API
 * NOTE: This creates a shortcut that opens Mini App INSIDE Telegram, not a standalone PWA!
 * 
 * For standalone PWA installation, user must:
 * 1. Open the app in external browser (Safari/Chrome)
 * 2. Use browser's "Add to Home Screen" feature
 */
export function addMiniAppToHomeScreen(): void {
  const tg = getTelegram();
  if (tg?.addToHomeScreen) {
    tg.addToHomeScreen();
  }
}

/**
 * Check home screen status for Mini App
 * @returns Promise with status or null if not supported
 */
export function checkMiniAppHomeScreenStatus(): Promise<HomeScreenStatus | null> {
  return new Promise((resolve) => {
    const tg = getTelegram();
    if (!tg?.checkHomeScreenStatus) {
      resolve(null);
      return;
    }
    tg.checkHomeScreenStatus((status) => {
      resolve(status);
    });
  });
}

/**
 * App context type for adaptive UI
 * - miniapp: Running inside Telegram Mini App (has external header with back button)
 * - pwa: Running as installed PWA (standalone, no browser chrome)
 * - browser: Running in regular browser
 */
export type AppContext = "miniapp" | "pwa" | "browser";

/**
 * Determine the current app context for adaptive UI positioning
 * Used for logo/rays alignment that needs to account for Telegram header
 */
export function getAppContext(): AppContext {
  // 1. Telegram Mini App - has external header with back button (~44-52px)
  if (isTelegramMiniApp()) {
    return "miniapp";
  }

  // 2. PWA (standalone) - no external header, but no browser chrome either
  if (typeof window !== "undefined") {
    // Standard check
    if (window.matchMedia("(display-mode: standalone)").matches) {
      return "pwa";
    }
    // iOS Safari specific
    if ((navigator as { standalone?: boolean }).standalone === true) {
      return "pwa";
    }
  }

  // 3. Browser - default, no external header
  return "browser";
}

export type { TelegramWebApp };
